/**
 * ToolsHub - Business Print PrintLogic Hub
 * Public launcher page linking to every tool. Route: /hub
 * Kiosk links are locked behind the staff PIN — the key is never shipped in the bundle.
 */
import { useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Ruler, Wrench, Monitor, Truck, MonitorPlay, Presentation,
  ClipboardList, Send, KeyRound, ExternalLink, SquareStack, Layers, BookLock, X, Lock, Unlock, PackageCheck, Tags, BarChart3
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KIOSK_STORAGE = "hub_kiosk_key";

const BADGES = {
  login: { label: "Staff login", cls: "bg-amber-400/15 text-amber-300 ring-amber-400/30" },
  kiosk: { label: "One-tap", cls: "bg-cyan-400/15 text-cyan-300 ring-cyan-400/30" },
  locked: { label: "PIN unlock", cls: "bg-slate-400/15 text-slate-300 ring-slate-400/30" },
  pin: { label: "PIN", cls: "bg-emerald-400/15 text-emerald-300 ring-emerald-400/30" },
  guide: { label: "PIN", cls: "bg-fuchsia-400/15 text-fuchsia-300 ring-fuchsia-400/30" },
};

const SECTIONS = [
  {
    id: "shopfloor",
    title: "Shop Floor",
    accent: "#22d3ee",
    tools: [
      { id: "production-board", name: "Production Board", desc: "Main Kanban shop-floor board", icon: LayoutDashboard, href: "/", badge: "login" },
      { id: "wide-format", name: "Wide Format Dashboard", desc: "Wide format department board", icon: Ruler, href: "/wide-format", badge: "login" },
      { id: "work-terminal", name: "Operator Work Terminal", desc: "Start & track active jobs", icon: Wrench, href: "/work-terminal", badge: "login" },
    ],
  },
  {
    id: "displays",
    title: "Wall Displays & Kiosks",
    accent: "#34d399",
    tools: [
      { id: "kiosk-production", name: "Kiosk — Production", desc: "Production board wall display", icon: Monitor, kioskPath: "/kiosk/production", badge: "kiosk" },
      { id: "kiosk-delivery", name: "Kiosk — Delivery", desc: "Delivery whiteboard display", icon: Truck, kioskPath: "/kiosk/delivery", badge: "kiosk" },
      { id: "kiosk-wide-format", name: "Kiosk — Wide Format", desc: "Wide format dept display", icon: SquareStack, kioskPath: "/kiosk/wide-format-dept", badge: "kiosk" },
      { id: "kiosk-prepress", name: "Kiosk — Prepress", desc: "Prepress dept display", icon: Layers, kioskPath: "/kiosk/prepress-dept", badge: "kiosk" },
      { id: "delivery-projection", name: "Delivery Projection", desc: "Big-screen delivery view", icon: MonitorPlay, href: "/delivery-projection", badge: "login" },
      { id: "production-projection", name: "Production Projection", desc: "Big-screen production view", icon: Presentation, href: "/production-projection", badge: "login" },
    ],
  },
  {
    id: "office",
    title: "Office & Rep Tools",
    accent: "#f59e0b",
    tools: [
      { id: "sop-toolkit", name: "SOP Toolkit", desc: "Create jobs, artwork, box labels", icon: ClipboardList, href: "/sop-toolkit", badge: "pin" },
      { id: "box-labels", name: "Box Labels", desc: "Print box labels for any job number", icon: Tags, href: "/box-labels", badge: "pin" },
      { id: "dispatch-board", name: "Dispatch Board", desc: "Add & tick off deliveries, print run sheets", icon: PackageCheck, href: "/dispatch", badge: "pin" },
      { id: "delivery-request", name: "Delivery Request Form", desc: "Add delivery / collection requests", icon: Send, href: "/request-delivery", badge: "pin" },
    ],
  },
  {
    id: "management",
    title: "Management",
    accent: "#60a5fa",
    tools: [
      { id: "sales-dashboard", name: "Sales Dashboard", desc: "Orders written, customer POs & invoiced by month", icon: BarChart3, href: "/sales", badge: "login" },
    ],
  },
];

const ToolCard = ({ tool, accent, kioskKey, onLockedClick }) => {
  const Icon = tool.icon;
  const isKiosk = !!tool.kioskPath;
  const locked = isKiosk && !kioskKey;
  const badge = locked ? BADGES.locked : BADGES[tool.badge];
  const href = isKiosk ? `${tool.kioskPath}?key=${encodeURIComponent(kioskKey || "")}` : tool.href;

  const inner = (
    <>
      <span className="absolute left-0 top-0 h-full w-1 transition-all duration-200 group-hover:w-1.5" style={{ backgroundColor: accent }} />
      <span
        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ring-1 ring-white/10 transition-colors"
        style={{ backgroundColor: `${accent}1a`, color: accent }}
      >
        <Icon className="h-6 w-6" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-bold text-slate-100 truncate">{tool.name}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${badge.cls}`}>{badge.label}</span>
        </span>
        <span className="mt-0.5 block truncate text-sm text-slate-400">{tool.desc}</span>
      </span>
      {locked
        ? <Lock className="h-4 w-4 flex-shrink-0 text-slate-500 transition-colors group-hover:text-slate-200" />
        : <ExternalLink className="h-4 w-4 flex-shrink-0 text-slate-500 transition-colors group-hover:text-slate-200" />}
    </>
  );

  const cls = "group relative flex w-full items-center gap-4 rounded-xl bg-slate-800/60 ring-1 ring-white/5 p-4 pl-5 overflow-hidden text-left transition-all duration-200 hover:bg-slate-800 hover:ring-white/15 hover:-translate-y-0.5";

  if (locked) {
    return (
      <button type="button" onClick={() => onLockedClick(tool)} data-testid={`hub-tool-${tool.id}`} className={cls}>
        {inner}
      </button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`hub-tool-${tool.id}`} className={cls}>
      {inner}
    </a>
  );
};

const ToolsHub = () => {
  const [guidePin, setGuidePin] = useState(null); // null = closed, '' = open
  const [kioskKey, setKioskKey] = useState(() => sessionStorage.getItem(KIOSK_STORAGE) || "");
  const [unlockTool, setUnlockTool] = useState(null); // kiosk tool awaiting unlock
  const [unlockPin, setUnlockPin] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const downloadGuide = () => {
    if (!guidePin) return;
    window.open(`${BACKEND_URL}/api/sop/access-guide?pin=${encodeURIComponent(guidePin)}`, "_blank");
    setGuidePin(null);
  };

  const unlockKiosks = async () => {
    if (!unlockPin || unlocking) return;
    setUnlocking(true);
    setUnlockError("");
    try {
      const res = await axios.get(`${BACKEND_URL}/api/hub/kiosk-links?pin=${encodeURIComponent(unlockPin)}`);
      const key = res.data.kiosk_key;
      sessionStorage.setItem(KIOSK_STORAGE, key);
      setKioskKey(key);
      const path = unlockTool?.kioskPath;
      setUnlockTool(null);
      setUnlockPin("");
      if (path) window.open(`${path}?key=${encodeURIComponent(key)}`, "_blank");
    } catch (err) {
      setUnlockError(err.response?.status === 429 ? "Too many attempts — try again later." : "Wrong PIN, try again.");
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="tools-hub">
      {/* subtle grid backdrop */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.15]"
        style={{ backgroundImage: "linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)", backgroundSize: "44px 44px" }}
      />
      <div className="relative mx-auto max-w-5xl px-5 py-12">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 shadow-lg shadow-cyan-900/40">
              <LayoutDashboard className="h-7 w-7 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">Business Print</p>
              <h1 className="text-3xl font-black tracking-tight sm:text-4xl">PrintLogic Hub</h1>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-slate-400">
            Every shop-floor board, wall display and office tool in one place. Tap any card to open it in a new tab.
          </p>
        </header>

        {/* Sections */}
        <div className="space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.id} data-testid={`hub-section-${section.id}`}>
              <div className="mb-4 flex items-center gap-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: section.accent }} />
                <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-300">{section.title}</h2>
                <span className="h-px flex-1 bg-white/5" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {section.tools.map((tool) => (
                  <ToolCard key={tool.id} tool={tool} accent={section.accent} kioskKey={kioskKey} onLockedClick={setUnlockTool} />
                ))}
              </div>
            </section>
          ))}

          {/* Reference */}
          <section data-testid="hub-section-reference">
            <div className="mb-4 flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full bg-fuchsia-400" />
              <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-300">Reference</h2>
              <span className="h-px flex-1 bg-white/5" />
            </div>
            <button
              type="button"
              onClick={() => setGuidePin("")}
              data-testid="hub-tool-access-guide"
              className="group relative flex w-full items-center gap-4 overflow-hidden rounded-xl bg-slate-800/60 p-4 pl-5 text-left ring-1 ring-white/5 transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-800 hover:ring-white/15 sm:w-1/2"
            >
              <span className="absolute left-0 top-0 h-full w-1 bg-fuchsia-400 transition-all duration-200 group-hover:w-1.5" />
              <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-fuchsia-400/10 text-fuchsia-400 ring-1 ring-white/10">
                <BookLock className="h-6 w-6" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="font-bold text-slate-100">Access &amp; Password Guide</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${BADGES.guide.cls}`}>{BADGES.guide.label}</span>
                </span>
                <span className="mt-0.5 block text-sm text-slate-400">All URLs &amp; passwords (PDF)</span>
              </span>
              <KeyRound className="h-4 w-4 flex-shrink-0 text-slate-500 transition-colors group-hover:text-slate-200" />
            </button>
          </section>
        </div>

        <footer className="mt-14 border-t border-white/5 pt-6 text-center text-xs text-slate-500">
          Business Print — PrintLogic Shop Floor · Keep this page private
        </footer>
      </div>

      {/* Kiosk unlock PIN dialog */}
      {unlockTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="hub-kiosk-unlock-dialog">
          <div className="w-full max-w-sm rounded-2xl bg-slate-900 p-6 ring-1 ring-white/10">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-lg font-bold"><Unlock className="h-5 w-5 text-emerald-400" /> Unlock Kiosk Links</h3>
              <button onClick={() => { setUnlockTool(null); setUnlockPin(""); setUnlockError(""); }} className="text-slate-400 hover:text-white" aria-label="Close" data-testid="hub-kiosk-unlock-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-3 text-sm text-slate-400">Enter the staff PIN once to unlock all kiosk wall-display links for this session.</p>
            <input
              type="password"
              autoFocus
              value={unlockPin}
              onChange={(e) => { setUnlockPin(e.target.value); setUnlockError(""); }}
              onKeyDown={(e) => e.key === "Enter" && unlockKiosks()}
              placeholder="Enter PIN"
              data-testid="hub-kiosk-unlock-pin-input"
              className="w-full rounded-xl border-2 border-slate-700 bg-slate-800 px-4 py-3 text-slate-100 placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
            />
            {unlockError && <p className="mt-2 text-sm text-rose-400" data-testid="hub-kiosk-unlock-error">{unlockError}</p>}
            <button
              onClick={unlockKiosks}
              disabled={!unlockPin || unlocking}
              data-testid="hub-kiosk-unlock-btn"
              className="mt-4 w-full rounded-xl bg-emerald-600 py-3 font-bold text-white transition-colors hover:bg-emerald-700 disabled:bg-slate-700"
            >
              {unlocking ? "Checking…" : "Unlock & Open"}
            </button>
          </div>
        </div>
      )}

      {/* Access Guide PIN dialog */}
      {guidePin !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="hub-guide-dialog">
          <div className="w-full max-w-sm rounded-2xl bg-slate-900 p-6 ring-1 ring-white/10">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Access Guide</h3>
              <button onClick={() => setGuidePin(null)} className="text-slate-400 hover:text-white" aria-label="Close" data-testid="hub-guide-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-3 text-sm text-slate-400">This document contains all passwords. Enter the SOP PIN to download.</p>
            <input
              type="password"
              autoFocus
              value={guidePin}
              onChange={(e) => setGuidePin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && downloadGuide()}
              placeholder="Enter PIN"
              data-testid="hub-guide-pin-input"
              className="w-full rounded-xl border-2 border-slate-700 bg-slate-800 px-4 py-3 text-slate-100 placeholder-slate-500 focus:border-fuchsia-500 focus:outline-none"
            />
            <button
              onClick={downloadGuide}
              disabled={!guidePin}
              data-testid="hub-guide-download-btn"
              className="mt-4 w-full rounded-xl bg-fuchsia-600 py-3 font-bold text-white transition-colors hover:bg-fuchsia-700 disabled:bg-slate-700"
            >
              Download Guide
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolsHub;
